import React, { useState } from 'react';
import { Input, Button, Select, Dropdown, MenuProps } from 'antd';

import './PageScaffoldControls.css';

interface ActionButton {
  label: string | React.ReactNode;
  type?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
  props?: React.ComponentProps<typeof Button>;
  buttonType?: string;
  items?: MenuProps['items'] | undefined;
  render?: () => React.ReactNode;
}

export interface PageScaffoldFilterSetting {
  placeholder?: string;
  onFilter: (filter: string | undefined) => void;
  values: {
    label: string;
    value: string;
  }[];
  selectedValue: string | undefined | null;
  clearable?: boolean;
  style?: React.CSSProperties;
}

export type PageScaffoldFilterSettings =
  | PageScaffoldFilterSetting
  | PageScaffoldFilterSetting[];

interface PageScaffoldControlsProps {
  countryAddonBefore?: boolean;
  searchLabel: string;
  searchDefaultValue?: string;
  onSearch: (input: string, addonBeforeQuery: string) => void;
  isLoading: boolean;
  onSearchCancel: () => void;
  hideSearch?: boolean;
  actionButtons: ActionButton[];
  filterSettings?: PageScaffoldFilterSettings;
}

const PageScaffoldControls: React.FC<PageScaffoldControlsProps> = ({
  countryAddonBefore,
  searchLabel,
  searchDefaultValue,
  onSearch,
  isLoading,
  onSearchCancel,
  hideSearch,
  actionButtons,
  filterSettings
}) => {
  const [searchInput, setSearchInput] = useState<string>(searchDefaultValue ?? '');
  const [selectedCountry, setSelectedCountry] = useState<string>('33600000000');
  const filterSettingsList = Array.isArray(filterSettings)
    ? filterSettings
    : filterSettings
      ? [filterSettings]
      : [];

  const handleSearch = () => {
    if (searchInput.length > 0) {
      onSearch(searchInput, selectedCountry);
    }
  };

  return (
    <div className="page-scaffold-controls">
      <div className="page-scaffold-controls__inputs">
        <div className="page-scaffold-controls__inputs__input">
          {!hideSearch && (
            <Input.Search
              addonBefore={
                countryAddonBefore && (
                  <Select
                    value={selectedCountry}
                    onChange={(value) => setSelectedCountry(value)}
                  >
                    <Select.Option value="33600000000">
                      <span role="img" aria-label="France">
                        🇫🇷
                      </span>
                    </Select.Option>
                    <Select.Option value="32400000000">
                      <span role="img" aria-label="Belgium">
                        🇧🇪
                      </span>
                    </Select.Option>
                    <Select.Option value="41700000000">
                      <span role="img" aria-label="Switzerland">
                        🇨🇭
                      </span>
                    </Select.Option>
                  </Select>
                )
              }
              placeholder={searchLabel}
              value={searchInput}
              style={{ width: 306 }}
              onChange={(e) => {
                setSearchInput(e.target.value);
                if (e.target.value === '') {
                  onSearchCancel();
                }
              }}
              onSearch={handleSearch}
              loading={isLoading}
            />
          )}
        </div>
        {filterSettingsList.map((filterSetting, index) => (
          <div
            className="page-scaffold-controls__inputs__input"
            key={filterSetting.placeholder ?? `filter-${index}`}
          >
            <Select
              placeholder={filterSetting.placeholder ?? ''}
              disabled={isLoading}
              allowClear={filterSetting.clearable ?? false}
              onChange={(value) => {
                filterSetting.onFilter(value);
              }}
              value={filterSetting.selectedValue}
              style={{ width: '100%', ...filterSetting.style }}
            >
              {filterSetting.values.map((valItem) => (
                <Select.Option key={valItem.value} value={valItem.value}>
                  {valItem.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        ))}
      </div>
      <div className="page-scaffold-controls__action-buttons">
        {actionButtons.map((btn, index) => (
          btn.render ? (
            <React.Fragment key={index}>{btn.render()}</React.Fragment>
          ) : btn.buttonType === 'dropdown' ?
            <Dropdown key={index} menu={{ items: btn.items }} placement="bottomRight" arrow={{ pointAtCenter: true }}>
              <Button {...btn.props} type={btn.type}>{btn.label}</Button>
            </Dropdown> :
            <Button key={index} {...btn.props} type={btn.type}>
              {btn.label}
            </Button>
        ))}
      </div>
    </div>
  );
};

export default PageScaffoldControls;
